import React, { FC } from 'react';

import Col from 'antd/es/col';
import Popover from 'antd/es/popover';

import { FALLBACK_IMAGE_URL } from '@constants/constants';
import Markdown from '@components/utils/Markdown';
import ImageWithFallback from '@components/utils/ImageWithFallback';

import ShowMoreCard, { ArrowAlign } from '../ShowMoreCard';
import {
  PromotionsGrid,
  Wrapper,
  Logo,
  Description,
  Seperator,
  Title,
  PromotionTitle,
  PromotionPopoverContent,
} from './OpportunityCardStyle';
export interface Promotions {
  name: string;
  description: string;
  image: string;
}
export interface OpportunityCardProps {
  title: string;
  onNavigate: () => void;
  items: Promotions[];
  isShowMore?: boolean;
  showInstallments?: () => void;
  showKocAilem?: boolean;
  isBankInstallments?: boolean;
}

const KOC_AILEM = 'Koç Ailem';

const OpportunityCard: FC<OpportunityCardProps> = ({
  title,
  onNavigate,
  items,
  isShowMore = false,
  showInstallments,
  showKocAilem,
  isBankInstallments,
}) => {
  const kocAilemIndex = items.findIndex((item) => item.name === KOC_AILEM);
  const expandablePromotionCount = (() => {
    if (items.length && kocAilemIndex < 3 && !showKocAilem)
      return items.length - 4;
    else if (items.length && kocAilemIndex < 3 && showKocAilem)
      return items.length - 3;
    else return 0;
  })();
  const hasExpandablePromotions = expandablePromotionCount > 0;

  const renderPromotion = (
    item: Promotions,
    index: number
  ): React.ReactElement | null => {
    if (item.name === KOC_AILEM && !showKocAilem) {
      return null;
    }

    const promotionCard = (
      <Wrapper key={index} tabIndex={item.description?.trim() ? 0 : undefined}>
        <ImageWithFallback
          Component={Logo}
          alt={item.name}
          src={item.image}
          fallbackImage={FALLBACK_IMAGE_URL}
          checkInitialLoad
        />
        <Seperator />
        <Col>
          <PromotionTitle>{item.name}</PromotionTitle>
        </Col>
      </Wrapper>
    );

    if (!item.description?.trim()) return promotionCard;

    return (
      <Popover
        key={index}
        title={item.name}
        content={
          <PromotionPopoverContent>
            <Markdown text={item.description} />
          </PromotionPopoverContent>
        }
        trigger={['hover', 'click', 'focus']}
        placement='top'
        getPopupContainer={(triggerNode) =>
          triggerNode.parentElement || document.body
        }
        styles={{
          body: {
            maxWidth: '340px',
            padding: '10px 12px',
          },
        }}
      >
        {promotionCard}
      </Popover>
    );
  };

  const initialPromotionCount = kocAilemIndex < 3 && !showKocAilem ? 4 : 3;

  return (
    <ShowMoreCard
      title={title}
      navigationText={
        hasExpandablePromotions
          ? !isShowMore
            ? `+${expandablePromotionCount} Fırsatı Görüntüle`
            : `+${expandablePromotionCount} Fırsatı Kapat`
          : ''
      }
      navigationStyle={{
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 0,
      }}
      onNavigate={onNavigate}
      isShowMore={isShowMore}
      arrowAlign={
        hasExpandablePromotions ? ArrowAlign.UP_DOWN : ArrowAlign.NO_ARROW
      }
    >
      <PromotionsGrid
        data-testid='promotions-grid'
        $hasNavigation={hasExpandablePromotions}
      >
        {items
          .slice(0, initialPromotionCount)
          .map((item, index) => renderPromotion(item, index))}

        {isBankInstallments ? (
          <Wrapper isButton={true} onClick={showInstallments}>
            <Logo
              alt='Kredi Kartı Taksitleri'
              src='/static/images/hotel-detail/credit-card.svg'
            />
            <Seperator />
            <Col>
              <Title>Kredi Kartı Taksitleri</Title>
              <Description>
                <Markdown text='Diğer Taksit Seçenekleri' />
              </Description>
            </Col>
          </Wrapper>
        ) : null}
        {isShowMore
          ? items
              .slice(initialPromotionCount)
              .map((item, index) =>
                renderPromotion(item, index + initialPromotionCount)
              )
          : null}
      </PromotionsGrid>
    </ShowMoreCard>
  );
};

export default OpportunityCard;
